import {useEffect, useState} from 'react';

import {FormState, initialFieldState, initialFormState} from '../components/Form';
import {Field, FieldConditionalLogicRule, Form as FormType} from '../types';
import {Flow, FlowFetchResponse} from '../_services/Flow';
import {calculationFieldValues, fieldValueText} from '../_helpers/form';

/**
 * Interface for the return value of useForm custom hook.
 * @interface UseFormReturn
 *
 * @property {FormType} form
 * FormType interface defining the shape of a form.
 *
 * @property {FormState} formState
 * FormState interface defining the state of a form.
 *
 * @property {React.Dispatch<React.SetStateAction<FormState>>} setFormState
 * A dispatch function from React's useState which allows setting a new form state.
 *
 * @property {boolean} formIsLoading
 * State variable that is true when the form is currently loading data.
 *
 * @property {string} formLoadErrorMessage
 * String to hold form load error message, if any occurs during form data fetch.
 *
 * @property {boolean} formSubmitButtonDisabled
 * State variable that determines whether the form's submit button is disabled.
 */
interface UseFormReturn {
    form: FormType,
    formState: FormState,
    setFormState: React.Dispatch<React.SetStateAction<FormState>>,
    formIsLoading: boolean,
    formLoadErrorMessage: string,
    formSubmitButtonDisabled: boolean
}

/**
 * Custom hook to manage form state and fetch form data from the server
 * @param {string} formId - The unique identifier for the form
 * @param {string} [overrideQueryString=''] - Optional query string to override the default behavior of reading from the location
 * @returns {UseFormReturn} Object containing form data and state management functions
 */
export const useForm = (
    formId: string,
    overrideQueryString: string = ''
): UseFormReturn => {
    const [formState, setFormState] = useState<FormState>({
        ...initialFormState,
        fieldValues: {},
    });
    const [form, setForm]: any = useState<FormType>({} as FormType);
    const [formIsLoading, setFormIsLoading] = useState<boolean>(false);
    const [formLoadErrorMessage, setFormLoadErrorMessage] = useState<string>('');
    const [formSubmitButtonDisabled, setFormSubmitButtonDisabled] = useState<boolean>(false);

    useEffect(() => {
        setFormIsLoading(true);
        setFormLoadErrorMessage('');

        // TODO handle this... somehow
        // setFormShowConfirmtaion(false);
        // setConfirmationMessage('');

        // if query string is passed to useEffect, use this
        // otherwise read from location
        const queryString = overrideQueryString || window.location.search;

        Flow.getInstance().getForm(formId, queryString)
            .then((response: FlowFetchResponse) => {
                if (response.ok && response.json.form) {
                    const form: FormType = response.json.form;

                    // if form does not have the show_in_form_list flag set, redirect to homepage
                    // always allow the support request form (93)
                    console.log('checking form ', form.id, form.flow_show_in_form_list);
                    if (form?.flow_show_in_form_list !== 'Y' && form?.id != '93') {
                        window.location.href = '/p/flow/';
                        return;
                    }

                    // initialize the formstate
                    const newFormState = {
                        ...formState,
                        fieldValues: form.fields.reduce((acc: any, field: Field) => {
                            const initialValue: any = field.initialValue;
                            if (field.type === 'select') {
                                if (typeof initialValue === 'object') {
                                    if (initialValue.hasOwnProperty('label')) {
                                        initialValue.label = initialValue?.label?.replace('&amp;', '&');
                                    }
                                    if (initialValue.hasOwnProperty('text')) {
                                        initialValue.text = initialValue?.text?.replace('&amp;', '&');
                                    }
                                }
                            }

                            return {...acc, [field.name]: initialValue};
                        }, {}),
                        fieldState: form.fields.reduce((acc: any, field: Field) => {
                            return {...acc, [field.name]: {...initialFieldState}}
                        })
                    };

                    // initially calculate any fields
                    const calculatedFields = calculationFieldValues(form, newFormState, form.calculationFields);

                    setFormState({
                        ...newFormState,
                        fieldValues: {
                            ...newFormState.fieldValues,
                            ...calculatedFields
                        }
                    });
                    setForm(form);
                } else {
                    throw Error(response?.json?.message);
                }
            })
            .catch((error: any) => {
                setFormLoadErrorMessage(error?.message || 'Exception loading form');
            })
            .finally(() => {
                setFormIsLoading(false);
            })
    }, [formId]);

    useEffect(() => {
        if (form?.button?.conditionalLogic) {
            let shouldShow = true;
            let matchCount = 0;
            form?.button?.conditionalLogic?.rules.forEach((rule: FieldConditionalLogicRule) => {
                const targetFieldValue = fieldValueText(formState, form, rule.fieldId);

                if (['is', 'isnot'].includes(rule.operator)) {
                    const valueIsEqualToTargetField = (
                        (targetFieldValue instanceof Array && targetFieldValue.find((item: string) => item === rule.value) !== undefined) ||
                        targetFieldValue === rule.value
                    );

                    if ((rule.operator === 'is' || rule.operator === 'contains') && valueIsEqualToTargetField) {
                        matchCount++;
                    } else if (rule.operator === 'isnot' && !valueIsEqualToTargetField) {
                        matchCount++;
                    }
                } else if (rule.operator === 'starts_with' && typeof targetFieldValue === 'string' && (new RegExp('^' + rule.value, 'i')).test(targetFieldValue as string)) {
                    matchCount++;
                } else if (rule.operator === 'contains' && typeof targetFieldValue === 'string' && (new RegExp(rule.value, 'i')).test(targetFieldValue as string)) {
                    matchCount++;
                } else if ((rule.operator === '>' || rule.operator === '<') && targetFieldValue && !(targetFieldValue instanceof Array)) {
                    const numberValue = targetFieldValue.replace(/[$,]/g, '');

                    if (
                        (rule.operator === '>' && numberValue > rule.value) ||
                        (rule.operator === '<' && numberValue < rule.value)
                    ) {
                        matchCount++;
                    }
                }
            });

            const ruleTrue = (
                (form?.button?.conditionalLogic.logicType === 'all' && matchCount === form?.button?.conditionalLogic.rules.length) ||
                (form?.button?.conditionalLogic.logicType === 'any' && matchCount > 0)
            );
            shouldShow = ((!ruleTrue && form?.button?.conditionalLogic.actionType === 'hide') || (ruleTrue && form?.button?.conditionalLogic.actionType === 'show'));

            setFormSubmitButtonDisabled(!shouldShow);
        }
    }, [formState]);

    return {
        form,
        formState,
        setFormState,
        formIsLoading,
        formLoadErrorMessage,
        formSubmitButtonDisabled
    };
}